import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";

import { useGetProfessionalReferences } from "../api/useGetProfessionalReferences";
import { PROFESSIONAL_REFERENCE_STATUS } from "../constants";
import { type ProfessionalReference, type ProfessionalReferenceStatus } from "../types";
import { ReferenceCard } from "./ReferenceCard";
import { ReferenceCardSkeleton } from "./ReferenceCardSkeleton";

export function ApprovedViewReferencesList() {
  const { data, isLoading } = useGetProfessionalReferences();

  const referencesByStatus = data?.references.reduce<
    Partial<Record<ProfessionalReferenceStatus, ProfessionalReference[]>>
  >((accumulator, reference) => {
    if (!isDefined(accumulator[reference.status])) {
      accumulator[reference.status] = [];
    }
    accumulator[reference.status]?.push(reference);
    return accumulator;
  }, {});

  // Display verified references first, then declined references
  const referencesToShow = isDefined(referencesByStatus)
    ? [
        ...(referencesByStatus[PROFESSIONAL_REFERENCE_STATUS.VERIFIED] ?? []),
        ...(referencesByStatus[PROFESSIONAL_REFERENCE_STATUS.DECLINED] ?? []),
      ]
    : undefined;

  const title = (
    <Text semibold variant="h5" sx={(theme) => ({ color: theme.palette.text.secondary })}>
      Submitted References:
    </Text>
  );

  // Show skeleton cards while loading
  if (isLoading) {
    return (
      <Stack gap={4}>
        {title}
        <Stack gap={6}>
          <ReferenceCardSkeleton completed />
          <ReferenceCardSkeleton completed />
        </Stack>
      </Stack>
    );
  }

  // Don't render anything if no references to show
  if (!isDefined(referencesToShow) || referencesToShow.length === 0) {
    return null;
  }

  return (
    <Stack gap={4}>
      {title}
      <Stack gap={6}>
        {referencesToShow.map((reference) => (
          <ReferenceCard key={reference._id} completed reference={reference} />
        ))}
      </Stack>
    </Stack>
  );
}
